import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from 'shared/components/Icon';
import axiosInstance from 'shared/services/api/axiosInstance';
import {
  getNumericCountOperators,
  getDidOperators,
  getMeasureTypeLabel,
  getAggregateTypes,
  getNumericEventProperties,
} from 'features/activity/config/userFilterProperties';
import { getDataTypeIcon } from 'features/activity/components/UserPropertyFilterModule';
import WhoTimeFilterDropdown from 'features/activity/components/WhoTimeFilterDropdown';

/**
 * Parse filter field to extract source, eventName/contractMethod, measureType, aggregateType, aggregateProperty
 */
function parseWhoFilterField(field) {
  if (!field) return null;
  // event_count:eventName
  if (field.startsWith('event_count:')) {
    const eventName = field.replace('event_count:', '');
    return { source: 'event', eventName, contractMethod: null, measureType: 'total_events', aggregateType: null, aggregateProperty: null };
  }
  // event_agg:eventName:aggType:propertyName
  if (field.startsWith('event_agg:')) {
    const rest = field.replace('event_agg:', '');
    const parts = rest.split(':');
    const eventName = parts[0] || '';
    const aggregateType = parts[1] || 'sum';
    const aggregateProperty = parts.slice(2).join(':') || null;
    return { source: 'event', eventName, contractMethod: null, measureType: 'aggregate_property', aggregateType, aggregateProperty };
  }
  return null;
}

/**
 * Build field string from parsed state
 */
function buildFieldFromParsed(parsed) {
  if (!parsed) return null;
  if (parsed.measureType === 'total_events') return `event_count:${parsed.eventName}`;
  if (parsed.measureType === 'aggregate_property' && parsed.source === 'event') {
    return `event_agg:${parsed.eventName}:${parsed.aggregateType}:${parsed.aggregateProperty || ''}`;
  }
  return null;
}

const OperatorDropdown = ({ value, operators, onChange, minWidth = '100px' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const safeOperators = Array.isArray(operators) && operators.length > 0 ? operators : [{ value: 'did', label: 'Did' }];
  const selectedOperator = safeOperators.find((op) => op.value === value) || safeOperators[0];

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 cursor-pointer hover:bg-surface-hover transition-colors"
        style={{ minWidth }}
      >
        {selectedOperator.label}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              className="fixed z-[9999] bg-surface border border-line-strong rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${Math.max(position.width || 120, 120)}px`,
              }}
            >
              <div className="py-1">
                {safeOperators.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => { onChange(op.value); setIsOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-sunken flex items-center ${value === op.value ? 'bg-surface-hover text-[color:var(--text-primary)]' : ''}`}
                  >
                    <span>{op.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * Aggregator dropdown: Total Events | Aggregate property (with hover submenu)
 */
const AggregatorDropdown = ({ parsed, onUpdate }) => {
  const [mainOpen, setMainOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const mainRef = useRef(null);
  const subRef = useRef(null);
  const [mainPos, setMainPos] = useState({ top: 0, left: 0, width: 0 });
  const [subPos, setSubPos] = useState({ top: 0, left: 0 });

  const measureType = parsed?.measureType || 'total_events';
  const aggregateTypes = getAggregateTypes();
  const selectedAggregate = aggregateTypes.find((a) => a.value === parsed?.aggregateType);

  const totalLabel = getMeasureTypeLabel('event', 'total_events');

  const handleSelectTotal = () => {
    const next = { ...parsed, source: 'event', measureType: 'total_events', aggregateType: null, aggregateProperty: null };
    const field = buildFieldFromParsed(next);
    if (field) onUpdate({ field, property: field });
    setMainOpen(false);
  };

  const handleSelectAggregateType = (aggType) => {
    const next = { ...parsed, source: 'event', measureType: 'aggregate_property', aggregateType: aggType, aggregateProperty: parsed?.aggregateProperty || null };
    const field = buildFieldFromParsed(next);
    if (field) onUpdate({ field, property: field });
    setSubOpen(false);
    setMainOpen(false);
  };

  useEffect(() => {
    if (mainOpen && mainRef.current) {
      const rect = mainRef.current.getBoundingClientRect();
      setMainPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 140) });
    }
  }, [mainOpen]);

  useEffect(() => {
    if (subOpen && subRef.current) {
      const rect = subRef.current.getBoundingClientRect();
      setSubPos({ top: rect.top, left: rect.right + 4 });
    }
  }, [subOpen]);

  const displayLabel = measureType === 'aggregate_property' && selectedAggregate
    ? `Aggregate: ${selectedAggregate.label}`
    : totalLabel;

  return (
    <>
      <button
        ref={mainRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setMainOpen((o) => !o); setSubOpen(false); }}
        onMouseEnter={() => { if (mainOpen) setSubOpen(false); }}
        className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 cursor-pointer hover:bg-surface-hover transition-colors"
        style={{ minWidth: '120px' }}
      >
        {displayLabel}
      </button>
      {mainOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9997]" onClick={() => { setMainOpen(false); setSubOpen(false); }} />
            <div
              className="fixed z-[9998] bg-surface border border-line-strong rounded shadow-lg py-1"
              style={{ top: `${mainPos.top}px`, left: `${mainPos.left}px`, width: `${mainPos.width}px` }}
            >
              <button
                type="button"
                onClick={handleSelectTotal}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-sunken ${measureType !== 'aggregate_property' ? 'bg-surface-hover' : ''}`}
              >
                {totalLabel}
              </button>
              <div
                ref={subRef}
                className="relative"
                onMouseEnter={() => setSubOpen(true)}
                onMouseLeave={() => setSubOpen(false)}
              >
                <button
                  type="button"
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-sunken ${measureType === 'aggregate_property' ? 'bg-surface-hover' : ''}`}
                >
                  Aggregate property
                </button>
                {subOpen &&
                  createPortal(
                    <>
                      <div
                        className="fixed z-[9999] bg-surface border border-line-strong rounded shadow-lg py-1 min-w-[140px]"
                        style={{ top: `${subPos.top}px`, left: `${subPos.left}px` }}
                        onMouseEnter={() => setSubOpen(true)}
                        onMouseLeave={() => setSubOpen(false)}
                      >
                        {aggregateTypes.map((a) => (
                          <button
                            key={a.value}
                            type="button"
                            onClick={() => handleSelectAggregateType(a.value)}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-sunken ${parsed?.aggregateType === a.value ? 'bg-surface-hover' : ''}`}
                          >
                            {a.label}
                          </button>
                        ))}
                      </div>
                    </>,
                    document.body
                  )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * Property selector dropdown (numeric properties only, shown when aggregate_property)
 */
const PropertySelectorDropdown = ({ parsed, teamId, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [numericProperties, setNumericProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const buttonRef = useRef(null);

  const getNumericTeamId = () => {
    let id = typeof teamId === 'object' && teamId !== null ? parseInt(teamId._id || teamId.id, 10) : parseInt(teamId, 10);
    if (typeof teamId === 'string' && isNaN(id)) {
      try {
        const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        id = parseInt(td._id || td.id, 10);
      } catch (_) {}
    }
    return isNaN(id) ? null : id;
  };

  useEffect(() => {
    if (!isOpen || !parsed || parsed.measureType !== 'aggregate_property') return;
    const numericTeamId = getNumericTeamId();
    const baseProps = getNumericEventProperties();

    if (parsed.source === 'event' && parsed.eventName && numericTeamId) {
      setLoading(true);
      axiosInstance
        .get('/events/custom-properties', { params: { team_id: numericTeamId, event_name: parsed.eventName, limit: 200 } })
        .then((res) => {
          const custom = (res.data?.custom_properties || [])
            .filter((p) => (p.type || 'string') === 'number')
            .map((p) => ({ name: p.name, label: p.name, type: 'number', source: 'event', isCustom: true }));
          setNumericProperties([...baseProps, ...custom]);
        })
        .catch(() => setNumericProperties(baseProps))
        .finally(() => setLoading(false));
    } else {
      setNumericProperties(baseProps);
    }
  }, [isOpen, parsed?.source, parsed?.eventName, teamId]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 160) });
    }
  }, [isOpen]);

  const selectedProp = numericProperties.find((p) => p.name === parsed?.aggregateProperty);
  const displayLabel = selectedProp ? selectedProp.label : 'Select property...';

  const handleSelect = (prop) => {
    const next = { ...parsed, aggregateProperty: prop.name };
    const field = buildFieldFromParsed(next);
    if (field) onUpdate({ field, property: field });
    setIsOpen(false);
  };

  if (parsed?.measureType !== 'aggregate_property') return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 cursor-pointer hover:bg-surface-hover transition-colors"
        style={{ minWidth: '140px' }}
      >
        {displayLabel}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              className="fixed z-[9999] bg-surface border border-line-strong rounded shadow-lg max-h-48 overflow-y-auto py-1"
              style={{ top: `${position.top}px`, left: `${position.left}px`, width: `${position.width}px` }}
            >
              {loading ? (
                <div className="px-3 py-2 text-xs text-[color:var(--text-secondary)]">Loading...</div>
              ) : numericProperties.length === 0 ? (
                <div className="px-3 py-2 text-xs text-[color:var(--text-secondary)]">No numeric properties</div>
              ) : (
                numericProperties.map((prop) => (
                  <button
                    key={prop.name}
                    type="button"
                    onClick={() => handleSelect(prop)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-sunken ${parsed?.aggregateProperty === prop.name ? 'bg-surface-hover' : ''}`}
                  >
                    {prop.label}
                  </button>
                ))
              )}
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * Filter chip for WHO block (event counts): WHO → Did/Did not → Event → Total/Aggregate → [Property if aggregate] → operator → value.
 */
const UserWhoFilterChip = ({
  filter,
  isFirst,
  onEdit,
  onRemove,
  onUpdate,
  teamId,
}) => {
  const connector = filter.connector || (isFirst ? 'WHO' : 'AND');
  const type = filter.propertyType || 'number';
  const DataTypeIcon = getDataTypeIcon(type);
  const operators = getNumericCountOperators();
  const didOperators = getDidOperators();
  const defaultOp = operators[0]?.value || 'equal';
  const currentOperator = filter.operator || defaultOp;
  const currentDidOperator = filter.didOperator || 'did';

  const field = filter.field || filter.property;
  const parsed = parseWhoFilterField(field) || {
    source: 'event',
    eventName: field?.startsWith('event_') ? (filter.propertyLabel || '').split(':').pop() : null,
    contractMethod: null,
    measureType: 'total_events',
    aggregateType: null,
    aggregateProperty: null,
  };

  if (!parsed.eventName && parsed.source === 'event' && filter.propertyLabel) parsed.eventName = filter.propertyLabel;

  const handleChipUpdate = (updates) => {
    onUpdate(updates);
  };

  const renderValueInput = () => {
    if (currentOperator === 'is_numeric' || currentOperator === 'is_not_numeric') return null;
    if (currentOperator === 'between' || currentOperator === 'not_between') {
      return (
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={filter.value ?? ''}
            onChange={(e) => handleChipUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
            placeholder="From..."
            className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-20"
            onClick={(e) => e.stopPropagation()}
          />
          <span className="text-xs text-[color:var(--text-secondary)]">to</span>
          <input
            type="number"
            value={filter.value2 ?? ''}
            onChange={(e) => handleChipUpdate({ value2: e.target.value === '' ? null : parseFloat(e.target.value) })}
            placeholder="To..."
            className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-20"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      );
    }
    return (
      <input
        type="number"
        value={filter.value ?? ''}
        onChange={(e) => handleChipUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
        placeholder="Value"
        className="px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 hover:bg-surface-hover transition-colors w-28"
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  const pillLabel = parsed.eventName;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isFirst ? (
        <span className="text-xs font-medium text-[color:var(--text-secondary)] px-2">WHO</span>
      ) : (
        <OperatorDropdown
          value={connector}
          operators={[{ value: 'AND', label: 'AND' }, { value: 'OR', label: 'OR' }]}
          onChange={(c) => handleChipUpdate({ connector: c })}
          minWidth="60px"
        />
      )}
      <OperatorDropdown
        value={currentDidOperator}
        operators={didOperators}
        onChange={(d) => handleChipUpdate({ didOperator: d })}
        minWidth="90px"
      />
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-surface-hover rounded-lg cursor-pointer hover:bg-surface-hover transition-colors"
      >
        <DataTypeIcon className="h-4 w-4 text-[color:var(--text-secondary)] flex-shrink-0" />
        <span
          className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-surface-hover text-[color:var(--text-secondary)] flex-shrink-0"
          title="Event"
        >
          E
        </span>
        <span className="text-sm text-[color:var(--text-primary)] font-medium">{pillLabel || filter.propertyLabel}</span>
      </div>
      <AggregatorDropdown parsed={parsed} onUpdate={handleChipUpdate} />
      <PropertySelectorDropdown parsed={parsed} teamId={teamId} onUpdate={handleChipUpdate} />
      <OperatorDropdown
        value={currentOperator}
        operators={operators}
        onChange={(op) => handleChipUpdate({ operator: op, value2: null })}
        minWidth="100px"
      />
      {renderValueInput()}
      <WhoTimeFilterDropdown
        value={filter.eventTimeFilter}
        onChange={(eventTimeFilter) => handleChipUpdate({ eventTimeFilter })}
      />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1.5 text-[color:var(--text-secondary)] hover:text-[color:var(--text-secondary)] transition-colors"
        title="Delete filter"
      >
        <Icon name="delete" size={16} />
      </button>
    </div>
  );
};

export default UserWhoFilterChip;
